import { apiClient } from '../../apiHelper';

export type QuickInferenceStatus = 'pending' | 'success' | 'fail';

export type QuickInferenceSessionStatus =
  | 'pending'
  | 'processing'
  | 'success'
  | 'partial'
  | 'fail';

export interface QuickInferenceFileSummary {
  id: string;
  filename: string;
  content_type: string;
  status: QuickInferenceStatus;
  number_of_frames: number;
  error_message?: string | null;
  pacs_ingest_status?: 'pending' | 'success' | 'fail' | 'skipped';
  created_at: string | null;
  updated_at: string | null;
}

export interface QuickInferenceSessionListItem {
  id: string;
  label: string | null;
  status: QuickInferenceSessionStatus;
  file_count: number;
  total_frames: number;
  pixel_spacing: number | null;
  created_at: string | null;
  updated_at: string | null;
  is_legacy_orphan?: boolean;
  files: QuickInferenceFileSummary[];
}

/** @deprecated Use QuickInferenceSessionListItem — kept for orphan compatibility */
export interface QuickInferenceListItem {
  id: string;
  filename: string;
  content_type: string;
  status: QuickInferenceStatus;
  number_of_frames: number;
  created_at: string | null;
  updated_at: string | null;
}

export interface QuickInferenceSegmentation {
  id?: number | string;
  category_id: number;
  type?: string;
  geometry_type?: string;
  label?: string | null;
  points?: string;
  area?: number | null;
  perimeter?: number | null;
  length?: number | null;
  score?: number;
  segment_type?: string | null;
  is_manual?: boolean;
}

export interface QuickInferenceFrame {
  segmentations?: QuickInferenceSegmentation[];
  views?: string[];
  /** User-confirmed view codes (still matched after recompute). */
  user_views?: string[];
  /** View-def ids currently being filled via Assign (incomplete). */
  pending_user_views?: number[];
  quality_metrics?: Record<string, Record<string, number | boolean>>;
  source_frame_number?: number | null;
}

export interface QuickInferenceViewValidationCheck {
  category_id: number;
  display_name: string;
  rule_type: 'visible' | 'not_visible' | string;
  required_for_match: boolean;
  max_structures: number;
  is_satisfied: boolean;
  has_conflict: boolean;
  all_segs?: Array<{ seg_id?: string | number; is_manual?: boolean; geometry_type?: string }>;
  user_seg_id?: string | number | null;
  ml_seg_id?: string | number | null;
}

export interface QuickInferenceViewValidation {
  success: boolean;
  view_def_id: number;
  view_code?: string | null;
  display_name?: string | null;
  frame_ml_view_def_ids?: number[];
  checks: QuickInferenceViewValidationCheck[];
  all_required_satisfied: boolean;
  conflict_count: number;
  satisfied?: number;
  total?: number;
}

export interface QuickInferenceResultPayload {
  source_type?: string;
  frames?: Record<string, QuickInferenceFrame>;
  metadata?: {
    views?: Record<string, { frame_indices: string[]; total_frames: number }>;
    structures?: Record<string, { frame_indices: string[]; total_frames: number }>;
    total_frames?: number;
    total_unique_views?: number;
    total_unique_structures?: number;
    fps?: number;
    pixel_spacing?: number;
  };
  filtered?: Record<string, string[]>;
}

export interface QuickInferenceDetail {
  id: string;
  filename: string;
  content_type: string;
  pixel_spacing: number | null;
  status: QuickInferenceStatus;
  number_of_frames: number;
  error_message: string | null;
  result: QuickInferenceResultPayload;
  pacs_ingest_status?: 'pending' | 'success' | 'fail' | 'skipped';
  pacs_ingest_error?: string | null;
  created_at: string | null;
  updated_at: string | null;
}

export interface QuickInferenceSessionDetail extends QuickInferenceSessionListItem {}

const BASE = '/quick-inference';

export interface QuickInferenceSessionListResponse {
  items: QuickInferenceSessionListItem[];
  total: number;
  limit: number;
  offset: number;
  status_counts: {
    all: number;
    active: number;
    success: number;
    failed: number;
  };
}

export type QuickInferenceSessionStatusFilter = 'all' | 'active' | 'success' | 'failed';

export async function listQuickInferenceSessions(params?: {
  limit?: number;
  offset?: number;
  includeFiles?: boolean;
  status?: QuickInferenceSessionStatusFilter;
  search?: string;
}): Promise<QuickInferenceSessionListResponse> {
  const response = await apiClient.get(`${BASE}/sessions`, {
    limit: params?.limit ?? 20,
    offset: params?.offset ?? 0,
    include_files: params?.includeFiles ?? false,
    status: params?.status && params.status !== 'all' ? params.status : undefined,
    search: params?.search?.trim() || undefined,
  });
  const data = response?.data ?? {};
  return {
    items: data.items ?? [],
    total: data.total ?? 0,
    limit: data.limit ?? params?.limit ?? 20,
    offset: data.offset ?? params?.offset ?? 0,
    status_counts: data.status_counts ?? { all: 0, active: 0, success: 0, failed: 0 },
  };
}

export async function getQuickInferenceSession(
  sessionId: string
): Promise<QuickInferenceSessionDetail | null> {
  try {
    const response = await apiClient.get(
      `${BASE}/sessions/${encodeURIComponent(sessionId)}`
    );
    return response?.data ?? null;
  } catch (error: any) {
    if (error?.response?.status === 404) {
      return null;
    }
    throw error;
  }
}

export async function createQuickInferenceSession(params: {
  pixelSpacing?: number;
  pixelSpacingUnit?: 'mm' | 'cm';
  label?: string;
}): Promise<{ session_id: string; status: QuickInferenceSessionStatus }> {
  const formData = new FormData();
  formData.append('pixel_spacing', String(params.pixelSpacing ?? -1));
  formData.append('pixel_spacing_unit', params.pixelSpacingUnit ?? 'mm');
  if (params.label) {
    formData.append('label', params.label);
  }

  const response = await apiClient.postUpload(`${BASE}/sessions`, formData);
  return response?.data?.data ?? response?.data;
}

export async function uploadQuickInferenceSessionFile(params: {
  sessionId: string;
  file: File;
  pixelSpacing?: number;
  pixelSpacingUnit?: 'mm' | 'cm';
  onUploadProgress?: (loaded: number, total: number) => void;
}): Promise<{ id: string; status: QuickInferenceStatus }> {
  const formData = new FormData();
  formData.append('file', params.file);
  formData.append('pixel_spacing', String(params.pixelSpacing ?? -1));
  formData.append('pixel_spacing_unit', params.pixelSpacingUnit ?? 'mm');

  const response = await apiClient.postUpload(
    `${BASE}/sessions/${encodeURIComponent(params.sessionId)}/files`,
    formData,
    {
      onUploadProgress: (event) => {
        if (!params.onUploadProgress) return;
        const total = event.total && event.total > 0 ? event.total : params.file.size;
        params.onUploadProgress(event.loaded ?? 0, total);
      },
    }
  );
  return response?.data?.data ?? response?.data;
}

export async function deleteQuickInferenceSession(sessionId: string): Promise<void> {
  await apiClient.delete(`${BASE}/sessions/${encodeURIComponent(sessionId)}`);
}

export async function getQuickInference(
  id: string
): Promise<QuickInferenceDetail | null> {
  try {
    const response = await apiClient.get(`${BASE}/${encodeURIComponent(id)}`);
    return response?.data ?? null;
  } catch (error: any) {
    if (error?.response?.status === 404) {
      return null;
    }
    throw error;
  }
}

export async function deleteQuickInference(id: string): Promise<void> {
  await apiClient.delete(`${BASE}/${encodeURIComponent(id)}`);
}

export async function fetchQuickInferenceFrameUrl(
  id: string,
  frameNumber: number
): Promise<string> {
  const response = await apiClient.downloadFile(
    `${BASE}/${encodeURIComponent(id)}/frame/${frameNumber}`
  );
  return URL.createObjectURL(response.data);
}

export interface SaveQuickInferenceFrameSegmentationsPayload {
  segmentations: Array<{
    id?: number | string;
    category_id?: number;
    label?: string | null;
    geometry_type?: string;
    type?: string;
    segment_type?: string;
    points: string;
    length?: number;
    perimeter?: number;
    area?: number;
    score?: number;
    is_manual?: boolean;
  }>;
  delete_segmentation_ids?: Array<number | string>;
}

export async function saveQuickInferenceFrameSegmentations(
  inferenceId: string,
  frameKey: string,
  payload: SaveQuickInferenceFrameSegmentationsPayload
): Promise<{
  segmentations: QuickInferenceSegmentation[];
  views?: string[];
  user_views?: string[];
  pending_user_views?: number[];
  quality_metrics?: QuickInferenceFrame['quality_metrics'];
  removed_views?: string[];
  metadata?: QuickInferenceResultPayload['metadata'];
}> {
  const response = await apiClient.patch(
    `${BASE}/${encodeURIComponent(inferenceId)}/frames/${encodeURIComponent(frameKey)}`,
    payload as unknown as Record<string, unknown>
  );
  return (response as any)?.data ?? response;
}

export async function getQuickInferenceFrameViewValidation(
  inferenceId: string,
  frameKey: string,
  viewDefId: number
): Promise<QuickInferenceViewValidation> {
  const response = await apiClient.get(
    `${BASE}/${encodeURIComponent(inferenceId)}/frames/${encodeURIComponent(frameKey)}/view-validation/${viewDefId}`
  );
  return (response as any)?.data ?? response;
}

export type QuickInferenceUserViewAction = 'pending' | 'confirm' | 'remove';

export async function updateQuickInferenceFrameUserViews(
  inferenceId: string,
  frameKey: string,
  payload: {
    action: QuickInferenceUserViewAction;
    view_def_id?: number;
    view_code?: string;
  }
): Promise<{
  views?: string[];
  user_views?: string[];
  pending_user_views?: number[];
  segmentations?: QuickInferenceSegmentation[];
  quality_metrics?: QuickInferenceFrame['quality_metrics'];
}> {
  const response = await apiClient.put(
    `${BASE}/${encodeURIComponent(inferenceId)}/frames/${encodeURIComponent(frameKey)}/user-views`,
    payload as unknown as Record<string, unknown>
  );
  return (response as any)?.data ?? response;
}

export async function updateQuickInferencePixelSpacing(
  id: string,
  params: { pixelSpacing: number; pixelSpacingUnit?: 'mm' | 'cm' }
): Promise<QuickInferenceDetail> {
  const response = await apiClient.patch(`${BASE}/${encodeURIComponent(id)}`, {
    pixel_spacing: params.pixelSpacing,
    pixel_spacing_unit: params.pixelSpacingUnit ?? 'mm',
  });
  return response?.data ?? response;
}

export interface QuickInferenceFileSpacing {
  pixelSpacing?: number;
  pixelSpacingUnit?: 'mm' | 'cm';
}

export async function createQuickInferencesBatch(params: {
  files: File[];
  pixelSpacing?: number;
  pixelSpacingUnit?: 'mm' | 'cm';
  getPixelSpacingForFile?: (file: File) => QuickInferenceFileSpacing;
  concurrency?: number;
  onFileStart?: (file: File, index: number) => void;
  onFileUploadProgress?: (file: File, index: number, loaded: number, total: number) => void;
  onProgress?: (
    completed: number,
    total: number,
    file: File,
    result: { id: string; status: QuickInferenceStatus } | { error: string },
    index: number
  ) => void;
}): Promise<{
  sessionId: string;
  results: Array<{ file: File; id?: string; status?: QuickInferenceStatus; error?: string }>;
}> {
  const session = await createQuickInferenceSession({
    pixelSpacing: params.pixelSpacing,
    pixelSpacingUnit: params.pixelSpacingUnit,
  });

  const { runWithConcurrency } = await import('../../upload/runWithConcurrency');
  const concurrency = params.concurrency ?? 3;
  let completed = 0;

  const results = await runWithConcurrency(params.files, concurrency, async (file, index) => {
    const spacing = params.getPixelSpacingForFile?.(file) ?? {
      pixelSpacing: params.pixelSpacing,
      pixelSpacingUnit: params.pixelSpacingUnit,
    };
    params.onFileStart?.(file, index);
    try {
      const result = await uploadQuickInferenceSessionFile({
        sessionId: session.session_id,
        file,
        pixelSpacing: spacing.pixelSpacing,
        pixelSpacingUnit: spacing.pixelSpacingUnit,
        onUploadProgress: (loaded, total) => {
          params.onFileUploadProgress?.(file, index, loaded, total);
        },
      });
      completed += 1;
      params.onProgress?.(completed, params.files.length, file, result, index);
      return { file, id: result.id, status: result.status };
    } catch (err: any) {
      const errorMsg = err?.response?.data?.detail || err?.message || 'Upload failed';
      completed += 1;
      params.onProgress?.(completed, params.files.length, file, { error: errorMsg }, index);
      return { file, error: errorMsg };
    }
  });

  return { sessionId: session.session_id, results };
}
